/**
 * 全屏观看时的播放控制条：分页、自动播放、循环、设置、退出全屏/退出组件
 * 宽度 w-full，高度 h-12，背景 bg-surface，左右布局
 * 设置面板使用内联定位（非 Portal），避免全屏时 Portal 挂到 body 被全屏层遮挡。
 */

import { useRef, useEffect, useState } from 'react';
import { Button, cn } from '@heroui/react';
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPlay,
  IconPlayerPause,
  IconRepeat,
  IconRepeatOff,
  IconSettings,
  IconArrowsDiagonalMinimize2,
  IconLogout,
} from '@tabler/icons-react';

const TOOLBAR_HEIGHT = 48; // h-12 = 12 * 4px

export interface PlaybackControlBarProps {
  /** 当前画布序号（1-based） */
  currentPage: number;
  /** 画布总数 */
  totalPages: number;
  /** 上一张 */
  onPrev: () => void;
  /** 下一张 */
  onNext: () => void;
  /** 是否自动播放 */
  autoPlay: boolean;
  /** 切换自动播放 */
  onAutoPlayChange: (v: boolean) => void;
  /** 是否循环播放 */
  loop: boolean;
  /** 切换循环播放 */
  onLoopChange: (v: boolean) => void;
  /** 播放间隔（秒），1–30，默认 3 */
  playbackIntervalSeconds: number;
  /** 播放间隔变更 */
  onPlaybackIntervalChange: (v: number) => void;
  /** 退出全屏 */
  onExitFullscreen: () => void;
  /** 退出组件（关闭画布/返回） */
  onExitComponent?: () => void;
  className?: string;
}

export function PlaybackControlBar({
  currentPage,
  totalPages,
  onPrev,
  onNext,
  autoPlay,
  onAutoPlayChange,
  loop,
  onLoopChange,
  playbackIntervalSeconds,
  onPlaybackIntervalChange,
  onExitFullscreen,
  onExitComponent,
  className,
}: PlaybackControlBarProps) {
  const clampedInterval = Math.max(1, Math.min(30, playbackIntervalSeconds));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!settingsOpen) return;
    const close = (e: MouseEvent) => {
      if (settingsWrapRef.current?.contains(e.target as Node)) return;
      setSettingsOpen(false);
    };
    document.addEventListener('mousedown', close, true);
    return () => document.removeEventListener('mousedown', close, true);
  }, [settingsOpen]);

  return (
    <div
      className={cn('flex h-12 w-full items-center justify-between bg-surface px-4', className)}
      style={{ minHeight: TOOLBAR_HEIGHT }}
    >
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          isIconOnly
          size="sm"
          aria-label="上一张"
          onPress={onPrev}
          isDisabled={totalPages <= 1}
          className="shrink-0"
        >
          <IconChevronLeft className="size-4" stroke={1.25} />
        </Button>
        <span className="min-w-[4rem] text-center text-sm tabular-nums text-default-foreground">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          isIconOnly
          size="sm"
          aria-label="下一张"
          onPress={onNext}
          isDisabled={totalPages <= 1}
          className="shrink-0"
        >
          <IconChevronRight className="size-4" stroke={1.25} />
        </Button>
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant={autoPlay ? 'primary' : 'outline'}
          isIconOnly
          size="sm"
          aria-label="自动播放"
          onPress={() => onAutoPlayChange(!autoPlay)}
          className="shrink-0"
        >
          <IconPlayerPlay className="size-4" stroke={1.25} />
        </Button>
        <Button
          variant={autoPlay ? 'outline' : 'primary'}
          isIconOnly
          size="sm"
          aria-label="暂停自动播放"
          onPress={() => onAutoPlayChange(false)}
          className="shrink-0"
        >
          <IconPlayerPause className="size-4" stroke={1.25} />
        </Button>
        <div className="w-2 shrink-0" aria-hidden />
        <Button
          variant={loop ? 'primary' : 'outline'}
          isIconOnly
          size="sm"
          aria-label="循环播放"
          onPress={() => onLoopChange(!loop)}
          className="shrink-0"
        >
          <IconRepeat className="size-4" stroke={1.25} />
        </Button>
        <Button
          variant={loop ? 'outline' : 'primary'}
          isIconOnly
          size="sm"
          aria-label="取消循环播放"
          onPress={() => onLoopChange(false)}
          className="shrink-0"
        >
          <IconRepeatOff className="size-4" stroke={1.25} />
        </Button>
        <div className="mx-2 h-5 w-px shrink-0 bg-divider" aria-hidden />
        <div ref={settingsWrapRef} className="relative shrink-0">
          <Button
            variant="outline"
            isIconOnly
            size="sm"
            aria-label="播放设置"
            aria-expanded={settingsOpen}
            className="shrink-0"
            onPress={() => setSettingsOpen((v) => !v)}
          >
            <IconSettings className="size-4" stroke={1.25} />
          </Button>
          {settingsOpen && (
            <div
              className="absolute bottom-full right-0 z-50 mb-2 w-max rounded-lg border border-divider bg-surface p-3 text-default-foreground shadow-md"
              role="dialog"
              aria-label="播放设置"
            >
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-sm text-default-foreground">播放间隔时间</span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    isIconOnly
                    size="sm"
                    className="h-8 w-8"
                    onPress={() => onPlaybackIntervalChange(Math.max(1, clampedInterval - 1))}
                  >
                    -
                  </Button>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={clampedInterval}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(30, Number.parseInt(e.target.value || '3', 10) || 3));
                      onPlaybackIntervalChange(n);
                    }}
                    className="h-8 w-16 rounded-md border border-divider bg-surface px-2 text-center text-sm"
                  />
                  <Button
                    variant="outline"
                    isIconOnly
                    size="sm"
                    className="h-8 w-8"
                    onPress={() => onPlaybackIntervalChange(Math.min(30, clampedInterval + 1))}
                  >
                    +
                  </Button>
                </div>
                <span className="shrink-0 text-sm text-default-foreground">秒</span>
              </div>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          isIconOnly
          size="sm"
          aria-label="返回编辑界面"
          onPress={onExitFullscreen}
          className="shrink-0"
        >
          <IconArrowsDiagonalMinimize2 className="size-4" stroke={1.25} />
        </Button>
        {onExitComponent != null && (
          <Button
            variant="outline"
            isIconOnly
            size="sm"
            aria-label="退出组件"
            onPress={onExitComponent}
            className="shrink-0"
          >
            <IconLogout className="size-4" stroke={1.25} />
          </Button>
        )}
      </div>
    </div>
  );
}

export { TOOLBAR_HEIGHT };
